//
// # DesignExplorer
//
// Left-panel component showing a hierarchical tree of available designs
// fetched from AlphaServer via the Workspace's listContentTree() method.
//
// The tree is returned pre-built by the server as DepotTreeNode[].
// Each node is either a library (folder with children) or a design (leaf
// with an ident). Clicking a leaf calls workspace.handleOpenRequest().
//

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import {
  Boxes,
  ChevronDown,
  ChevronRight,
  CircuitBoard,
  Component,
  FileCode,
  FilePlus,
  Filter,
  Folder,
  FolderInput,
  ExternalLink,
  FolderOpen,
  Copy,
  CopyPlus,
  LayoutGrid,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { schema } from "GenAlphaSchema";
import { Ident } from "GenAlphaModel";
import type { SymbolInfo } from "ServerInterface";
import type { OACatalog, OALibrary, OACell } from "../../workspace";
import {
  type DepotTreeNode,
  type DepotDesignNode,
  arePdkIdsCompatible,
} from "Depot";
import type { ActiveDesignInfo } from "../../types";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { FadeIn } from "../common/FadeIn";
import { extractErrorMessage } from "../../errorUtil";
import { usePersistentScroll } from "../common/usePersistentScroll";

// ------------------------------------------------------------------ //
// OpenAccess tree types                                                //
// ------------------------------------------------------------------ //

// A compiled, case-insensitive predicate that tests a full "/"-joined path
// against the user's filter string. See `buildPathMatcher`.
type PathMatcher = (path: string) => boolean;

// Build a path predicate from a filter string. Matching is a case-insensitive
// substring test against a node's full path, so queries can span path segments
// (e.g. "my_cell/schematic") rather than matching only a single segment.
//
// Wildcards:
//   *   matches any run of characters within a single path segment ("[^/]*")
//   **  matches any run of characters across segments (".*")
// All other characters are matched literally (regex metacharacters are escaped).
// With no wildcard, this is a plain substring test — the previous behavior.
function buildPathMatcher(filter: string): PathMatcher {
  const lower = filter.toLowerCase();
  if (!lower.includes("*")) {
    return (path) => path.toLowerCase().includes(lower);
  }
  // Escape every regex metacharacter (including "*"), then re-enable the
  // wildcards by rewriting the now-escaped "\*\*" and "\*" sequences. Order
  // matters: "**" must be handled before the single-"*" rule.
  const escaped = lower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = escaped.replace(/\\\*\\\*/g, ".*").replace(/\\\*/g, "[^/]*");
  // Unanchored, so the pattern still matches as a substring of the full path.
  const re = new RegExp(pattern);
  return (path) => re.test(path.toLowerCase());
}

// Check if an OA library or its descendants match the filter. `parentPath` is
// the "/"-joined path of ancestors (empty at the OpenAccess root). Matching is
// delegated to `match`, which tests each node's full path (see buildPathMatcher).
function oaLibraryMatchesFilter(
  lib: OALibrary,
  match: PathMatcher,
  parentPath: string
): boolean {
  const fullPath = parentPath ? `${parentPath}/${lib.name}` : lib.name;
  if (match(fullPath)) return true;
  return lib.cells.some((cell) => oaCellMatchesFilter(cell, match, fullPath));
}

function oaCellMatchesFilter(
  cell: OACell,
  match: PathMatcher,
  parentPath: string
): boolean {
  const fullPath = parentPath ? `${parentPath}/${cell.name}` : cell.name;
  if (match(fullPath)) return true;
  return cell.views.some((v) => match(`${fullPath}/${v}`));
}

// ------------------------------------------------------------------ //
// Filter helpers                                                       //
// ------------------------------------------------------------------ //

// Check if a node's full path, or any descendant's full path, matches the
// filter. `parentPath` is the "/"-joined path of ancestors (empty at the depot
// root). Matching is delegated to `match`, which tests the full path (see
// buildPathMatcher).
function nodeMatchesFilter(
  node: DepotTreeNode,
  match: PathMatcher,
  parentPath: string
): boolean {
  const fullPath = parentPath ? `${parentPath}/${node.name}` : node.name;
  if (match(fullPath)) return true;
  if (node.nodeKind === "library") {
    return node.children.some((child) =>
      nodeMatchesFilter(child, match, fullPath)
    );
  }
  return false;
}

// ------------------------------------------------------------------ //
// Compatibility filter helpers                                         //
// ------------------------------------------------------------------ //

// Check if a depot design node is compatible with the active design.
// Compatible means: content type can be instanced and PDK matches.
function isCompatibleDesign(
  node: DepotDesignNode,
  activeInfo: ActiveDesignInfo
): boolean {
  // Content type compatibility:
  // - Layout viewport can instance layouts
  // - Schematic viewport can instance symbols
  if (activeInfo.contentKind === "layout") {
    if (node.contentKind !== "layout") return false;
  } else if (activeInfo.contentKind === "schematic") {
    if (node.contentKind !== "symbol") return false;
  } else {
    // Symbol/graphics viewports don't support instancing
    return false;
  }
  // PDK compatibility: node matches active PDK, or node has no PDK (generic)
  if (!arePdkIdsCompatible(node.pdkId, activeInfo.pdkId)) {
    return false;
  }
  return true;
}

// Filter a depot tree node array, keeping library nodes if any descendant is compatible.
function filterCompatibleNodes(
  nodes: DepotTreeNode[],
  activeInfo: ActiveDesignInfo
): DepotTreeNode[] {
  return nodes.filter((node) => {
    if (node.nodeKind === "design") {
      return isCompatibleDesign(node, activeInfo);
    }
    // Library node: keep if any descendant is compatible
    return filterCompatibleNodes(node.children, activeInfo).length > 0;
  });
}

// ------------------------------------------------------------------ //
// PDK Primitives helpers                                               //
// ------------------------------------------------------------------ //

// A group of symbols for a specific PDK (or the built-in group for symbols with
// no pdkId).
interface PrimitiveGroup {
  pdkId: string; // Display label: pdkId, or BUILT_IN_KEY for built-ins
  // True for PDK-independent built-in primitives (symbols with no pdkId). These
  // are surfaced in a separate "Built-In Primitives" section from PDK ones.
  isBuiltIn: boolean;
  symbols: SymbolInfo[];
}

// Map key used to bucket symbols with no pdkId (the built-in primitives).
const BUILT_IN_KEY = "Generic";

// Group SymbolInfo[] into per-PDK groups plus a single built-in group (symbols
// with no pdkId), each sorted alphabetically.
function groupSymbolsByPdk(symbols: SymbolInfo[]): PrimitiveGroup[] {
  // Bucket by a discriminated key so built-ins never merge with a PDK — not even
  // one literally named BUILT_IN_KEY. Built-ins use a sentinel that cannot occur
  // as a pdkId (it contains a NUL); real PDKs are namespaced under a "pdk:"
  // prefix. Keying on the raw pdkId (with a plain fallback) would collide here.
  const BUILT_IN_BUCKET = "\u0000builtin";
  const groups = new Map<string, SymbolInfo[]>();
  for (const sym of symbols) {
    const key = sym.pdkId === undefined ? BUILT_IN_BUCKET : `pdk:${sym.pdkId}`;
    const list = groups.get(key);
    if (list) {
      list.push(sym);
    } else {
      groups.set(key, [sym]);
    }
  }
  return Array.from(groups.values())
    .map((syms) => ({
      // Every symbol in a bucket shares a pdkId (or all lack one), so the first
      // is representative for both the label and the built-in flag.
      pdkId: syms[0].pdkId ?? BUILT_IN_KEY, // Display label
      isBuiltIn: syms[0].pdkId === undefined,
      symbols: syms.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.pdkId.localeCompare(b.pdkId));
}

// Check if a primitive symbol is compatible with the active design.
function isCompatiblePrimitive(
  sym: SymbolInfo,
  activeInfo: ActiveDesignInfo
): boolean {
  // For layouts: only generators (hasParams) are useful
  // For schematics: all primitives are useful
  if (activeInfo.contentKind === "layout" && !sym.hasParams) return false;
  if (
    activeInfo.contentKind !== "layout" &&
    activeInfo.contentKind !== "schematic"
  )
    return false;
  // PDK match: symbol matches active PDK, or symbol is generic (no pdkId)
  if (!arePdkIdsCompatible(sym.pdkId, activeInfo.pdkId)) return false;
  return true;
}

// ------------------------------------------------------------------ //
// Ident-to-path helpers                                                //
// ------------------------------------------------------------------ //

// Convert an Ident to a name path for traversing the depot tree.
function identToNamePath(ident: schema.Ident): string[] {
  if (ident.kind === "hierpath") {
    return ident.path;
  } else if (ident.kind === "libcellview") {
    return [ident.lib, ident.cell, ident.view];
  }
  return [];
}

// Build a path key for a node at a given depth from its ancestors' names.
// e.g. for depth=2 with parentKey="lib1/cell1", name="layout" => "lib1/cell1/layout"
function buildPathKey(parentKey: string, name: string): string {
  return parentKey ? `${parentKey}/${name}` : name;
}

// ------------------------------------------------------------------ //
// Leaf icon by contentKind (legacy name fallback)                     //
// ------------------------------------------------------------------ //

function LeafIcon({
  name,
  contentKind,
}: {
  name: string;
  contentKind?: string;
}) {
  // contentKind is the authoritative type signal.
  if (contentKind === "circuit") return <Zap size={14} className="shrink-0" />;
  if (contentKind === "schematic")
    return <CircuitBoard size={14} className="shrink-0" />;
  if (contentKind === "layout")
    return <LayoutGrid size={14} className="shrink-0" />;
  if (contentKind === "symbol")
    return <Component size={14} className="shrink-0" />;
  if (contentKind === "graphics")
    return <FileCode size={14} className="shrink-0" />;

  // Legacy fallback: some older index entries have no contentKind. Guess from
  // the leaf name, which for legacy designs is often the old view word.
  const lower = name.toLowerCase();
  if (lower === "schematic")
    return <CircuitBoard size={14} className="shrink-0" />;
  if (lower === "layout") return <LayoutGrid size={14} className="shrink-0" />;
  return <FileCode size={14} className="shrink-0" />;
}

// ------------------------------------------------------------------ //
// Context menus                                                         //
// ------------------------------------------------------------------ //

// Close a portal menu when the user clicks outside it or presses Escape.
function useMenuDismiss<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  onClose: () => void
): void {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [ref, onClose]);
}

interface ContextMenuState {
  x: number;
  y: number;
  ident: schema.HierPathIdent;
  // Full design node for compatibility checks (may be null for OA nodes)
  designNode: DepotDesignNode | null;
}

function DesignContextMenu({
  menu,
  onClose,
  onOpen,
  onDelete,
  onDuplicate,
  onRename,
  onAddToDesign,
  addToDesignEnabled,
}: {
  menu: ContextMenuState;
  onClose: () => void;
  onOpen: (ident: schema.HierPathIdent) => void;
  onDelete: (ident: schema.HierPathIdent) => void;
  onDuplicate: (ident: schema.HierPathIdent) => void;
  onRename: (ident: schema.HierPathIdent) => void;
  onAddToDesign: (ident: schema.HierPathIdent) => void;
  addToDesignEnabled: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useMenuDismiss(ref, onClose);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-50 py-1 rounded shadow-lg border text-xs min-w-[140px]"
      style={{
        left: menu.x,
        top: menu.y,
        backgroundColor: "var(--color-ide-surface)",
        borderColor: "var(--color-ide-border)",
        color: "var(--color-ide-text)",
      }}
    >
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-text)" }}
        onClick={() => {
          onOpen(menu.ident);
          onClose();
        }}
      >
        <ExternalLink size={12} />
        Open Design
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={
          addToDesignEnabled
            ? { color: "var(--color-ide-text)" }
            : {
                color: "var(--color-ide-text-dim)",
                opacity: 0.5,
                cursor: "default",
              }
        }
        onClick={() => {
          if (addToDesignEnabled) {
            onAddToDesign(menu.ident);
            onClose();
          }
        }}
      >
        <FolderInput size={12} />
        Add To Design
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-text)" }}
        onClick={() => {
          const pathStr = menu.ident.path.join("/");
          void navigator.clipboard.writeText(pathStr);
          onClose();
        }}
      >
        <Copy size={12} />
        Copy Path
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-text)" }}
        onClick={() => {
          onDuplicate(menu.ident);
          onClose();
        }}
      >
        <CopyPlus size={12} />
        Duplicate...
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-text)" }}
        onClick={() => {
          onRename(menu.ident);
          onClose();
        }}
      >
        <Pencil size={12} />
        Rename...
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-error, #e53e3e)" }}
        onClick={() => {
          onDelete(menu.ident);
          onClose();
        }}
      >
        <Trash2 size={12} />
        Delete
      </button>
    </div>,
    document.body
  );
}

// ------------------------------------------------------------------ //
// Folder context menu                                                   //
// ------------------------------------------------------------------ //

interface FolderContextMenuState {
  x: number;
  y: number;
  // Slash-separated depot path of the right-clicked folder (excludes the
  // "depot/" UI prefix), used to seed the New Design dialog's Path field.
  path: string;
}

// Right-click menu for a depot folder (library node). Folders aren't designs,
// so the only action is creating a new design pre-scoped to this folder.
function FolderContextMenu({
  menu,
  onClose,
  onNewDesign,
}: {
  menu: FolderContextMenuState;
  onClose: () => void;
  onNewDesign: (path: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useMenuDismiss(ref, onClose);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-50 py-1 rounded shadow-lg border text-xs min-w-[140px]"
      style={{
        left: menu.x,
        top: menu.y,
        backgroundColor: "var(--color-ide-surface)",
        borderColor: "var(--color-ide-border)",
        color: "var(--color-ide-text)",
      }}
    >
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-text)" }}
        onClick={() => {
          onNewDesign(menu.path);
          onClose();
        }}
      >
        <FilePlus size={12} />
        New Design...
      </button>
    </div>,
    document.body
  );
}

// ------------------------------------------------------------------ //
// Primitive context menu                                                //
// ------------------------------------------------------------------ //

interface PrimitiveContextMenuState {
  x: number;
  y: number;
  sym: SymbolInfo;
}

function PrimitiveContextMenu({
  menu,
  onClose,
  onAddToDesign,
  addEnabled,
}: {
  menu: PrimitiveContextMenuState;
  onClose: () => void;
  onAddToDesign: (sym: SymbolInfo) => void;
  addEnabled: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useMenuDismiss(ref, onClose);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-50 py-1 rounded shadow-lg border text-xs min-w-[140px]"
      style={{
        left: menu.x,
        top: menu.y,
        backgroundColor: "var(--color-ide-surface)",
        borderColor: "var(--color-ide-border)",
        color: "var(--color-ide-text)",
      }}
    >
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={
          addEnabled
            ? { color: "var(--color-ide-text)" }
            : {
                color: "var(--color-ide-text-dim)",
                opacity: 0.5,
                cursor: "default",
              }
        }
        onClick={() => {
          if (addEnabled) {
            onAddToDesign(menu.sym);
            onClose();
          }
        }}
      >
        <FolderInput size={12} />
        Add To Design
      </button>
    </div>,
    document.body
  );
}

// ------------------------------------------------------------------ //
// Tree node component                                                  //
// ------------------------------------------------------------------ //

interface TreeNodeViewProps {
  node: DepotTreeNode;
  depth: number;
  pathKey: string;
  // "/"-joined depot hierarchy path of ancestors (excludes this node's name and
  // the "depot/" UI prefix). Used for full-path substring filtering.
  nodePath: string;
  filter: string;
  // Canonical path matcher for this render, built once by the parent from
  // `filter` and threaded down so every node shares one compiled regex.
  match: PathMatcher;
  onOpen: (ident: schema.HierPathIdent) => void;
  onSelect: (ident: schema.HierPathIdent | null) => void;
  selectedIdent: schema.Ident | null;
  // `fullPath` is the node's "/"-joined depot path (computed during render,
  // since folder nodes don't store their own path). Forwarded so a folder menu
  // can seed the New Design dialog.
  onContextMenu: (
    e: React.MouseEvent,
    node: DepotTreeNode,
    fullPath: string
  ) => void;
  ancestorMatched?: boolean;
  expandedKeys: ReadonlySet<string>;
  onToggleExpand: (pathKey: string) => void;
  scrollToPathKey: string | null;
}

function TreeNodeView({
  node,
  depth,
  pathKey,
  nodePath,
  filter,
  match,
  onOpen,
  onSelect,
  selectedIdent,
  onContextMenu,
  ancestorMatched,
  expandedKeys,
  onToggleExpand,
  scrollToPathKey,
}: TreeNodeViewProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const isFolder = node.nodeKind === "library";
  const children = isFolder ? node.children : [];
  const hasChildren = children.length > 0;
  const indent = depth * 12;

  // When filtering, auto-expand and only show matching subtrees.
  // If this node or an ancestor matched, show ALL children (match flows downward).
  // Otherwise, only show children whose subtree contains a match (match flows upward).
  const isExpanded = filter ? true : expandedKeys.has(pathKey);
  // Full depot path of this node, used for full-path (wildcard) matching.
  const fullPath = nodePath ? `${nodePath}/${node.name}` : node.name;
  const thisMatches = !!filter && match(fullPath);
  const showAllChildren = ancestorMatched || thisMatches;
  const visibleChildren =
    filter && !showAllChildren
      ? children.filter((c) => nodeMatchesFilter(c, match, fullPath))
      : children;

  // Whether this design leaf is the currently selected node. Only depot
  // (hierpath) selections highlight depot rows; OpenAccess (libcellview)
  // selections highlight OA rows instead (see OAViewNode).
  const isSelected =
    node.nodeKind === "design" &&
    selectedIdent !== null &&
    selectedIdent.kind === "hierpath" &&
    node.ident.path.join("/") === selectedIdent.path.join("/");

  // Scroll into view when this node is the scroll target
  const isScrollTarget = scrollToPathKey === pathKey;
  useEffect(() => {
    if (isScrollTarget && nodeRef.current) {
      nodeRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [isScrollTarget]);

  const handleClick = () => {
    if (node.nodeKind === "design") {
      onSelect(node.ident);
    } else if (hasChildren) {
      onToggleExpand(pathKey);
    }
  };

  const handleDoubleClick = () => {
    if (node.nodeKind === "design") {
      onOpen(node.ident);
    }
  };

  // Enable dragging for design leaf nodes
  const handleDragStart = (e: React.DragEvent) => {
    if (node.nodeKind !== "design") {
      e.preventDefault();
      return;
    }
    // Serialize the design node for the drop target
    const dragData = JSON.stringify({
      ident: node.ident,
      name: node.name,
      contentKind: node.contentKind,
      pdkId: node.pdkId,
    });
    e.dataTransfer.setData("application/x-genalpha-depot-node", dragData);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.stopPropagation();
      onToggleExpand(pathKey);
    }
  };

  // Tooltip: show full path for designs, name for libraries
  const tooltip =
    node.nodeKind === "design" ? node.ident.path.join(" / ") : node.name;

  const isDraggable = node.nodeKind === "design";

  return (
    <div>
      <div
        ref={nodeRef}
        draggable={isDraggable}
        onDragStart={handleDragStart}
        className={`flex items-center gap-1 py-0.5 pr-2 rounded cursor-pointer select-none
                   hover:bg-white/5 text-[var(--color-ide-text-muted)]
                   hover:text-[var(--color-ide-text)]`}
        style={{
          paddingLeft: `${8 + indent}px`,
          ...(isSelected
            ? {
                backgroundColor: "var(--color-ide-selection-bg)",
                color: "var(--color-ide-selection-text)",
              }
            : {}),
        }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={(e) => {
          onContextMenu(e, node, fullPath);
        }}
        title={tooltip}
      >
        {/* Expand chevron — always reserve space for alignment */}
        <span className="w-3 shrink-0" onClick={handleChevronClick}>
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            ))}
        </span>

        {/* Icon */}
        {isFolder ? (
          isExpanded ? (
            <FolderOpen
              size={14}
              className="shrink-0 text-[var(--color-ide-text-muted)]"
            />
          ) : (
            <Folder
              size={14}
              className="shrink-0 text-[var(--color-ide-text-muted)]"
            />
          )
        ) : (
          <LeafIcon
            name={node.name}
            contentKind={
              node.nodeKind === "design" ? node.contentKind : undefined
            }
          />
        )}

        {/* Label */}
        <span className="text-xs truncate font-mono">{node.name}</span>
      </div>

      {isExpanded && hasChildren && (
        <FadeIn>
          <div>
            {visibleChildren.map((child, i) => (
              <TreeNodeView
                key={`${child.name}-${i}`}
                node={child}
                depth={depth + 1}
                pathKey={buildPathKey(pathKey, child.name)}
                nodePath={fullPath}
                filter={filter}
                match={match}
                onOpen={onOpen}
                onSelect={onSelect}
                selectedIdent={selectedIdent}
                onContextMenu={onContextMenu}
                ancestorMatched={showAllChildren}
                expandedKeys={expandedKeys}
                onToggleExpand={onToggleExpand}
                scrollToPathKey={scrollToPathKey}
              />
            ))}
          </div>
        </FadeIn>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// OpenAccess tree components                                           //
// ------------------------------------------------------------------ //

interface OAContextMenuState {
  x: number;
  y: number;
  ident: schema.LibCellViewIdent;
}

// Right-click menu for an OpenAccess (lib, cell, view) item. Mirrors the depot
// DesignContextMenu's Open / Add To Design / Copy Path actions, but operates on a
// libcellview ident (OA items aren't depot designs, so Delete / Export don't apply).
function OAContextMenu({
  menu,
  onClose,
  onOpen,
  onAddToDesign,
  addToDesignEnabled,
}: {
  menu: OAContextMenuState;
  onClose: () => void;
  onOpen: (ident: schema.LibCellViewIdent) => void;
  onAddToDesign: (ident: schema.LibCellViewIdent) => void;
  addToDesignEnabled: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-50 py-1 rounded shadow-lg border text-xs min-w-[140px]"
      style={{
        left: menu.x,
        top: menu.y,
        backgroundColor: "var(--color-ide-surface)",
        borderColor: "var(--color-ide-border)",
        color: "var(--color-ide-text)",
      }}
    >
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-text)" }}
        onClick={() => {
          onOpen(menu.ident);
          onClose();
        }}
      >
        <ExternalLink size={12} />
        Open Design
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={
          addToDesignEnabled
            ? { color: "var(--color-ide-text)" }
            : {
                color: "var(--color-ide-text-dim)",
                opacity: 0.5,
                cursor: "default",
              }
        }
        onClick={() => {
          if (addToDesignEnabled) {
            onAddToDesign(menu.ident);
            onClose();
          }
        }}
      >
        <FolderInput size={12} />
        Add To Design
      </button>
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
        style={{ color: "var(--color-ide-text)" }}
        onClick={() => {
          const { lib, cell, view } = menu.ident;
          const pathStr = `${lib}/${cell}/${view}`;
          void navigator.clipboard.writeText(pathStr);
          onClose();
        }}
      >
        <Copy size={12} />
        Copy Path
      </button>
    </div>,
    document.body
  );
}

interface OAViewNodeProps {
  library: string;
  cell: string;
  view: string;
  depth: number;
  filter: string;
  match: PathMatcher;
  onOpen: (ident: schema.LibCellViewIdent) => void;
  onContextMenu: (e: React.MouseEvent, ident: schema.LibCellViewIdent) => void;
  expandedKeys: ReadonlySet<string>;
  selectedIdent: schema.Ident | null;
  scrollToPathKey: string | null;
  ancestorMatched?: boolean;
}

function OAViewNode({
  library,
  cell,
  view,
  depth,
  filter,
  match,
  onOpen,
  onContextMenu,
  selectedIdent,
  scrollToPathKey,
  ancestorMatched,
}: OAViewNodeProps) {
  const indent = depth * 12;
  const matches =
    !filter || !!ancestorMatched || match(`${library}/${cell}/${view}`);
  const pathKey = `oa/${library}/${cell}/${view}`;
  const nodeRef = useRef<HTMLDivElement>(null);

  // Whether this view is the currently selected node (OpenAccess selections are
  // libcellview idents; depot selections are hierpath — see TreeNodeView).
  const isSelected =
    selectedIdent !== null &&
    selectedIdent.kind === "libcellview" &&
    selectedIdent.lib === library &&
    selectedIdent.cell === cell &&
    selectedIdent.view === view;

  // Scroll into view when this view is the reveal target.
  const isScrollTarget = scrollToPathKey === pathKey;
  useEffect(() => {
    if (isScrollTarget && nodeRef.current) {
      nodeRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [isScrollTarget]);

  if (!matches && filter) return null;

  const ident: schema.LibCellViewIdent = {
    kind: "libcellview",
    lib: library,
    cell,
    view,
  };

  const handleClick = () => {
    onOpen(ident);
  };

  return (
    <div
      ref={nodeRef}
      className="flex items-center gap-1 py-0.5 pr-2 rounded cursor-pointer select-none
                 hover:bg-white/5 text-[var(--color-ide-text-muted)]
                 hover:text-[var(--color-ide-text)]"
      style={{
        paddingLeft: `${8 + indent}px`,
        ...(isSelected
          ? {
              backgroundColor: "var(--color-ide-selection-bg)",
              color: "var(--color-ide-selection-text)",
            }
          : {}),
      }}
      onClick={handleClick}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e, ident);
      }}
      title={`${library}/${cell}/${view}`}
    >
      <span className="w-3 shrink-0" />
      <LeafIcon name={view} />
      <span className="text-xs truncate font-mono">{view}</span>
    </div>
  );
}

interface OACellNodeProps {
  library: string;
  cell: OACell;
  depth: number;
  filter: string;
  match: PathMatcher;
  onOpen: (ident: schema.LibCellViewIdent) => void;
  onContextMenu: (e: React.MouseEvent, ident: schema.LibCellViewIdent) => void;
  expandedKeys: ReadonlySet<string>;
  onToggleExpand: (pathKey: string) => void;
  selectedIdent: schema.Ident | null;
  scrollToPathKey: string | null;
  ancestorMatched?: boolean;
}

function OACellNode({
  library,
  cell,
  depth,
  filter,
  match,
  onOpen,
  onContextMenu,
  expandedKeys,
  onToggleExpand,
  selectedIdent,
  scrollToPathKey,
  ancestorMatched,
}: OACellNodeProps) {
  const indent = depth * 12;
  const pathKey = `oa/${library}/${cell.name}`;
  const cellPath = `${library}/${cell.name}`;
  const hasChildren = cell.views.length > 0;

  const thisMatches = !!filter && match(cellPath);
  const showAllChildren = ancestorMatched || thisMatches;
  const isExpanded = filter ? true : expandedKeys.has(pathKey);

  // Filter views if filtering and no ancestor/this matched
  const visibleViews =
    filter && !showAllChildren
      ? cell.views.filter((v) => match(`${cellPath}/${v}`))
      : cell.views;

  if (!showAllChildren && visibleViews.length === 0 && filter) return null;

  const handleClick = () => {
    if (hasChildren) onToggleExpand(pathKey);
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.stopPropagation();
      onToggleExpand(pathKey);
    }
  };

  return (
    <div>
      <div
        className="flex items-center gap-1 py-0.5 pr-2 rounded cursor-pointer select-none
                   hover:bg-white/5 text-[var(--color-ide-text-muted)]
                   hover:text-[var(--color-ide-text)]"
        style={{ paddingLeft: `${8 + indent}px` }}
        onClick={handleClick}
        onContextMenu={(e) => {
          // Folders have no custom menu, but suppress the native browser menu so
          // explorer right-click behaves consistently before the EditorCore
          // global `document.oncontextmenu` handler is installed (on editor init).
          e.preventDefault();
        }}
        title={cell.name}
      >
        <span className="w-3 shrink-0" onClick={handleChevronClick}>
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            ))}
        </span>
        {isExpanded ? (
          <FolderOpen
            size={14}
            className="shrink-0 text-[var(--color-ide-text-muted)]"
          />
        ) : (
          <Folder
            size={14}
            className="shrink-0 text-[var(--color-ide-text-muted)]"
          />
        )}
        <span className="text-xs truncate font-mono">{cell.name}</span>
      </div>

      {isExpanded && hasChildren && (
        <FadeIn>
          <div>
            {visibleViews.map((view) => (
              <OAViewNode
                key={view}
                library={library}
                cell={cell.name}
                view={view}
                depth={depth + 1}
                filter={filter}
                match={match}
                onOpen={onOpen}
                onContextMenu={onContextMenu}
                expandedKeys={expandedKeys}
                selectedIdent={selectedIdent}
                scrollToPathKey={scrollToPathKey}
                ancestorMatched={showAllChildren}
              />
            ))}
          </div>
        </FadeIn>
      )}
    </div>
  );
}

interface OALibraryNodeProps {
  library: OALibrary;
  depth: number;
  filter: string;
  match: PathMatcher;
  onOpen: (ident: schema.LibCellViewIdent) => void;
  onContextMenu: (e: React.MouseEvent, ident: schema.LibCellViewIdent) => void;
  expandedKeys: ReadonlySet<string>;
  onToggleExpand: (pathKey: string) => void;
  selectedIdent: schema.Ident | null;
  scrollToPathKey: string | null;
  ancestorMatched?: boolean;
}

function OALibraryNode({
  library,
  depth,
  filter,
  match,
  onOpen,
  onContextMenu,
  expandedKeys,
  onToggleExpand,
  selectedIdent,
  scrollToPathKey,
  ancestorMatched,
}: OALibraryNodeProps) {
  const indent = depth * 12;
  const pathKey = `oa/${library.name}`;
  const hasChildren = library.cells.length > 0;

  const thisMatches = !!filter && match(library.name);
  const showAllChildren = ancestorMatched || thisMatches;
  const isExpanded = filter ? true : expandedKeys.has(pathKey);

  // Filter cells if filtering and no ancestor/this matched
  const visibleCells =
    filter && !showAllChildren
      ? library.cells.filter((c) => oaCellMatchesFilter(c, match, library.name))
      : library.cells;

  if (!showAllChildren && visibleCells.length === 0 && filter) return null;

  const handleClick = () => {
    if (hasChildren) onToggleExpand(pathKey);
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.stopPropagation();
      onToggleExpand(pathKey);
    }
  };

  return (
    <div>
      <div
        className="flex items-center gap-1 py-0.5 pr-2 rounded cursor-pointer select-none
                   hover:bg-white/5 text-[var(--color-ide-text-muted)]
                   hover:text-[var(--color-ide-text)]"
        style={{ paddingLeft: `${8 + indent}px` }}
        onClick={handleClick}
        onContextMenu={(e) => {
          // See OACellNode: suppress the native menu on folders for consistent
          // explorer right-click behavior regardless of editor-init state.
          e.preventDefault();
        }}
        title={library.name}
      >
        <span className="w-3 shrink-0" onClick={handleChevronClick}>
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            ))}
        </span>
        {isExpanded ? (
          <FolderOpen
            size={14}
            className="shrink-0 text-[var(--color-ide-text-muted)]"
          />
        ) : (
          <Folder
            size={14}
            className="shrink-0 text-[var(--color-ide-text-muted)]"
          />
        )}
        <span className="text-xs truncate font-mono">{library.name}</span>
      </div>

      {isExpanded && hasChildren && (
        <FadeIn>
          <div>
            {visibleCells.map((cell) => (
              <OACellNode
                key={cell.name}
                library={library.name}
                cell={cell}
                depth={depth + 1}
                filter={filter}
                match={match}
                onOpen={onOpen}
                onContextMenu={onContextMenu}
                expandedKeys={expandedKeys}
                onToggleExpand={onToggleExpand}
                selectedIdent={selectedIdent}
                scrollToPathKey={scrollToPathKey}
                ancestorMatched={showAllChildren}
              />
            ))}
          </div>
        </FadeIn>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// PDK Primitives tree components                                       //
// ------------------------------------------------------------------ //

// Leaf node for a single primitive symbol (e.g. nmos, pmos, res)
function PrimitiveLeafNode({
  sym,
  depth,
  onAddToDesign,
  onContextMenu: onCtxMenu,
  addEnabled,
  scrollToPathKey,
  pathKey,
}: {
  sym: SymbolInfo;
  depth: number;
  onAddToDesign: (sym: SymbolInfo) => void;
  onContextMenu: (e: React.MouseEvent, sym: SymbolInfo) => void;
  addEnabled: boolean;
  scrollToPathKey: string | null;
  pathKey: string;
}) {
  const indent = depth * 12;
  const nodeRef = useRef<HTMLDivElement>(null);
  const isScrollTarget = scrollToPathKey === pathKey;

  // Scroll into view when this node is the scroll target
  useEffect(() => {
    if (isScrollTarget && nodeRef.current) {
      nodeRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [isScrollTarget]);

  // Make draggable so primitives can be dropped onto the canvas.
  // Include the full SymbolInfo so the drop handler can open the params dialog directly.
  const handleDragStart = (e: React.DragEvent) => {
    const dragData = JSON.stringify({
      kind: "primitive",
      symbolName: sym.name,
      pdkId: sym.pdkId,
      hasParams: sym.hasParams,
      symbolInfo: sym,
    });
    e.dataTransfer.setData("application/x-genalpha-primitive", dragData);
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div
      ref={nodeRef}
      className="flex items-center gap-1 py-0.5 pr-2 rounded cursor-pointer select-none
                 hover:bg-white/5 text-[var(--color-ide-text-muted)]
                 hover:text-[var(--color-ide-text)]"
      style={{
        paddingLeft: `${8 + indent}px`,
      }}
      title={sym.description || sym.name}
      draggable
      onDragStart={handleDragStart}
      onContextMenu={(e) => {
        e.preventDefault();
        onCtxMenu(e, sym);
      }}
      onDoubleClick={() => {
        if (addEnabled) onAddToDesign(sym);
      }}
    >
      <span className="w-3 shrink-0" />
      <Zap size={12} className="shrink-0 text-[var(--color-ide-text-muted)]" />
      <span className="text-xs truncate font-mono">{sym.name}</span>
      {sym.hasParams && (
        <span className="text-[10px] text-[var(--color-ide-text-dim)] ml-auto shrink-0">
          params
        </span>
      )}
    </div>
  );
}

// Group node for a PDK category (e.g. "sky130", "Generic")
function PrimitiveGroupNode({
  group,
  depth,
  filter,
  match,
  expandedKeys,
  onToggleExpand,
  onAddToDesign,
  onContextMenu: onCtxMenu,
  addEnabled,
  scrollToPathKey,
}: {
  group: PrimitiveGroup;
  depth: number;
  filter: string;
  match: PathMatcher;
  expandedKeys: ReadonlySet<string>;
  onToggleExpand: (key: string) => void;
  onAddToDesign: (sym: SymbolInfo) => void;
  onContextMenu: (e: React.MouseEvent, sym: SymbolInfo) => void;
  addEnabled: boolean;
  scrollToPathKey: string | null;
}) {
  const indent = depth * 12;
  const pathKey = `primitives/${group.pdkId}`;
  const isExpanded = filter ? true : expandedKeys.has(pathKey);

  // Filter symbols by text search. Match against the full "pdkId/symbol" path so
  // queries can span the group and symbol name, consistent with the depot/OA
  // trees.
  const visibleSymbols = filter
    ? group.symbols.filter((s) => match(`${group.pdkId}/${s.name}`))
    : group.symbols;

  if (filter && visibleSymbols.length === 0) return null;

  return (
    <div>
      <div
        className="flex items-center gap-1 py-0.5 pr-2 rounded cursor-pointer select-none
                   hover:bg-white/5 text-[var(--color-ide-text-muted)]
                   hover:text-[var(--color-ide-text)]"
        style={{ paddingLeft: `${8 + indent}px` }}
        onClick={() => onToggleExpand(pathKey)}
      >
        <span className="w-3 shrink-0">
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        <Folder
          size={14}
          className="shrink-0 text-[var(--color-ide-text-muted)]"
        />
        <span className="text-xs truncate font-mono">{group.pdkId}</span>
      </div>
      {isExpanded && (
        <FadeIn>
          <div>
            {visibleSymbols.map((sym) => (
              <PrimitiveLeafNode
                key={sym.name}
                sym={sym}
                depth={depth + 1}
                onAddToDesign={onAddToDesign}
                onContextMenu={onCtxMenu}
                addEnabled={addEnabled}
                scrollToPathKey={scrollToPathKey}
                pathKey={`${pathKey}/${sym.name}`}
              />
            ))}
          </div>
        </FadeIn>
      )}
    </div>
  );
}

// Minimum time (ms) the refresh spinner stays visible so the user can
// see that a refresh occurred, even when the server responds instantly.
const REFRESH_MIN_SPIN_MS = 500;

// ------------------------------------------------------------------ //
// Main DesignExplorer component                                        //
// ------------------------------------------------------------------ //

export function DesignExplorer() {
  const workspace = useWorkspace();
  const {
    designTreeVersion,
    revealInDepotIdent,
    activeDesignInfo,
    activeIdent,
  } = useWorkspaceSnapshot();
  const [filter, setFilter] = useState("");
  const [compatFilter, setCompatFilter] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Lifted expansion state: set of path keys that are currently expanded.
  // Neither section is expanded by default - user must click to expand.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // One-shot scroll target: set when revealing a node, cleared after scroll fires.
  const [scrollToPathKey, setScrollToPathKey] = useState<string | null>(null);

  // Selection and context menu state for delete feature. Holds either a depot
  // hierpath ident or an OpenAccess libcellview ident — the highlighted node is
  // matched by kind in TreeNodeView (hierpath) and OAViewNode (libcellview).
  const [selectedIdent, setSelectedIdent] = useState<schema.Ident | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [folderContextMenu, setFolderContextMenu] =
    useState<FolderContextMenuState | null>(null);
  const [primContextMenu, setPrimContextMenu] =
    useState<PrimitiveContextMenuState | null>(null);
  const [oaContextMenu, setOaContextMenu] = useState<OAContextMenuState | null>(
    null
  );
  const treeRef = useRef<HTMLDivElement>(null);

  // Preserve the scroll position across dockview tab switches.  The
  // Radix ScrollArea.Viewport's scrollTop gets reset to 0 when dockview
  // reparents the panel's DOM.
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  usePersistentScroll(scrollViewportRef);

  // Depot state - lazy loaded when user expands "Depot" section
  const [roots, setRoots] = useState<DepotTreeNode[]>([]);
  const [depotLoading, setDepotLoading] = useState(false);
  const [depotLoaded, setDepotLoaded] = useState(false);
  const [depotError, setDepotError] = useState<string | null>(null);

  // Guards against overlapping depot-tree fetches. Rapid tree-change events
  // (e.g. a rename broadcasts design-removed + design-added back-to-back) would
  // otherwise each fire a full listContentTree round-trip. `inFlight` drops a
  // concurrent call; `pending` records that a change arrived mid-fetch so we
  // refresh exactly once more when the current fetch finishes — coalescing the
  // burst into (at most) one trailing refresh without losing updates. Refs, not
  // state, so the guard is synchronous across back-to-back event turns.
  const depotFetchInFlight = useRef(false);
  const depotFetchPending = useRef(false);

  // PDK Primitives state - lazy loaded when user expands the section
  const [primitiveGroups, setPrimitiveGroups] = useState<PrimitiveGroup[]>([]);
  const [primitivesLoading, setPrimitivesLoading] = useState(false);
  const [primitivesLoaded, setPrimitivesLoaded] = useState(false);

  // OpenAccess state - lazy loaded when user expands "OpenAccess" section
  const [catalog, setCatalog] = useState<OACatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogLoaded, setCatalogLoaded] = useState(false);

  // Whether the OA catalog (read path) came back with libraries. Gates the
  // OpenAccess *tree section* — this reflects the read/catalog path selected by
  // OA_REMOTE_TRANSPORT, which may be the legacy gRPC server.
  const oaAvailable = !!catalog && catalog.libraries.length > 0;

  // Load depot tree (called when user expands Depot section)
  // On refresh (roots already populated), keeps existing tree visible while
  // fetching so the panel doesn't blink. The spinner is held for at least
  // REFRESH_MIN_SPIN_MS so the user can see the refresh happened.
  const loadDepotTree = useCallback(async () => {
    // If a fetch is already running, don't start a second one — mark that
    // another refresh is due and let the in-flight call pick it up when it ends.
    if (depotFetchInFlight.current) {
      depotFetchPending.current = true;
      return;
    }
    depotFetchInFlight.current = true;
    // Read-and-clear the pending flag. Wrapped in a function so its boolean
    // return survives control-flow narrowing: assigning `.current = false`
    // directly in the loop would let TS conclude the ref is always falsy (it
    // can't see the WS event handler set it across the await).
    const takePending = (): boolean => {
      const had = depotFetchPending.current;
      depotFetchPending.current = false;
      return had;
    };
    try {
      let refreshAgain = false;
      do {
        setDepotLoading(true);
        const minSpin = new Promise((r) => setTimeout(r, REFRESH_MIN_SPIN_MS));
        const result = await workspace.listContentTree();
        if (result.err) {
          setDepotError(extractErrorMessage(result.val));
          setRoots([]);
        } else {
          setDepotError(null);
          setRoots(result.val);
        }
        await minSpin;
        // Loop again only if a tree-change event landed during this fetch.
        refreshAgain = takePending();
      } while (refreshAgain);
    } finally {
      setDepotLoading(false);
      setDepotLoaded(true);
      depotFetchInFlight.current = false;
    }
  }, [workspace]);

  // Load PDK primitives (called when user expands Primitives section)
  const loadPrimitives = useCallback(async () => {
    setPrimitivesLoading(true);
    const result = await workspace.listAvailableSymbols();
    if (result.ok) {
      setPrimitiveGroups(groupSymbolsByPdk(result.val));
    }
    setPrimitivesLoading(false);
    setPrimitivesLoaded(true);
  }, [workspace]);

  // Load OA catalog (called when user expands OpenAccess section)
  const loadOACatalog = useCallback(async () => {
    setCatalogLoading(true);
    const result = await workspace.listOACatalog();
    if (result.ok) {
      setCatalog(result.val);
    }
    setCatalogLoading(false);
    setCatalogLoaded(true);
  }, [workspace]);

  const onToggleExpand = useCallback(
    (pathKey: string) => {
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(pathKey)) {
          next.delete(pathKey);
        } else {
          next.add(pathKey);

          // Lazy load: fetch data when section is first expanded. The PDK
          // Primitives and Built-In Primitives sections share the same
          // underlying symbol list, so expanding either one loads it.
          if (
            (pathKey === "primitives" || pathKey === "builtin") &&
            !primitivesLoaded &&
            !primitivesLoading
          ) {
            void loadPrimitives();
          }
          if (pathKey === "depot" && !depotLoaded && !depotLoading) {
            void loadDepotTree();
          }
          if (pathKey === "openaccess" && !catalogLoaded && !catalogLoading) {
            void loadOACatalog();
          }
        }
        return next;
      });
    },
    [
      primitivesLoaded,
      primitivesLoading,
      loadPrimitives,
      depotLoaded,
      depotLoading,
      catalogLoaded,
      catalogLoading,
      loadDepotTree,
      loadOACatalog,
    ]
  );

  // Re-fetch depot tree when designTreeVersion changes (design saved/added/removed)
  // Only re-fetch if depot has been loaded before
  useEffect(() => {
    if (depotLoaded) {
      void loadDepotTree();
    }
  }, [designTreeVersion]); // eslint-disable-line react-hooks/exhaustive-deps -- loadDepotTree is stable (useCallback with workspace dep)

  // Re-fetch primitives when the active design changes (different PDK or content kind = different primitives)
  useEffect(() => {
    if (primitivesLoaded) {
      void loadPrimitives();
    }
  }, [activeDesignInfo?.pdkId, activeDesignInfo?.contentKind]); // eslint-disable-line react-hooks/exhaustive-deps -- loadPrimitives is stable (useCallback with workspace dep)

  // Eagerly load OA catalog on mount to determine if OpenAccess section should be shown.
  // Server returns empty catalog if OA is not enabled for this tenant.
  useEffect(() => {
    void loadOACatalog();
  }, [loadOACatalog]);

  // Handle reveal requests: expand ancestors, select item, scroll to it.
  // Only acts when revealInDepotIdent is set (non-null). Selection is the
  // single highlight mechanism — no separate reveal highlight.
  useEffect(() => {
    if (!revealInDepotIdent) return;

    const namePath = identToNamePath(revealInDepotIdent);
    if (namePath.length === 0) {
      workspace.clearRevealInDepot();
      return;
    }

    // Generator paths should reveal in the Primitives section instead of the Depot section.
    // Two formats:
    //   Direct:    ["generators", pdkId, deviceName]
    //   Generated: ["generated", "generators", pdkId, deviceName, configHash]
    const isDirectGenerator =
      namePath.length >= 3 && namePath[0] === "generators";
    const isGeneratedGenerator =
      namePath.length >= 4 &&
      namePath[0] === "generated" &&
      namePath[1] === "generators";
    const isGeneratorPath = isDirectGenerator || isGeneratedGenerator;

    if (revealInDepotIdent.kind === "libcellview") {
      // OpenAccess design: reveal in the OpenAccess tree, not the Depot tree.
      // OA items are keyed by (lib, cell, view); their tree keys are
      // "oa/<lib>", "oa/<lib>/<cell>", and the view leaf "oa/<lib>/<cell>/<view>".
      const { lib, cell, view } = revealInDepotIdent;

      // Ensure the catalog is loaded before trying to reveal within it.
      if (!catalogLoaded && !catalogLoading) {
        void loadOACatalog();
      }

      setExpandedKeys((prev) => {
        const next = new Set(prev);
        next.add("openaccess"); // Ensure the OpenAccess section is expanded
        next.add(`oa/${lib}`); // Expand the library
        next.add(`oa/${lib}/${cell}`); // Expand the cell (reveals its views)
        return next;
      });

      // Select (persistent highlight) and scroll to the view leaf — parity with
      // the depot reveal below.
      setSelectedIdent(revealInDepotIdent);
      setScrollToPathKey(`oa/${lib}/${cell}/${view}`);
    } else if (isGeneratorPath) {
      const pdkId = isGeneratedGenerator ? namePath[2] : namePath[1];
      const symName = isGeneratedGenerator ? namePath[3] : namePath[2];

      // Ensure primitives are loaded before trying to reveal.
      if (!primitivesLoaded && !primitivesLoading) {
        void loadPrimitives();
      }

      // Expand the Primitives section and the correct PDK group.
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        next.add("primitives");
        next.add(`primitives/${pdkId}`);
        return next;
      });

      // Scroll to the primitive (no persistent highlight — primitives
      // don't support selection).
      setScrollToPathKey(`primitives/${pdkId}/${symName}`);
    } else {
      // Regular depot design: expand depot section and ancestors.
      // Ensure the depot tree is loaded before trying to reveal within it.
      if (!depotLoaded && !depotLoading) {
        void loadDepotTree();
      }

      // Expand the "depot" section itself, then all ancestor nodes within it.
      // Tree path keys are prefixed with "depot/" (e.g. "depot/lib1/cell1").
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        next.add("depot"); // Ensure the Depot section is expanded
        let key = "depot";
        for (let i = 0; i < namePath.length - 1; i++) {
          key = `${key}/${namePath[i]}`;
          next.add(key);
        }
        return next;
      });

      // Select the item (provides persistent highlight) and scroll to it.
      if (revealInDepotIdent.kind === "hierpath") {
        setSelectedIdent(revealInDepotIdent);
      }
      setScrollToPathKey(`depot/${namePath.join("/")}`);
    }

    // Clear the reveal request from workspace so it doesn't re-trigger
    workspace.clearRevealInDepot();

    // Clear the scroll target after a delay so the scroll-into-view
    // effect has time to fire (the node may not be rendered yet if
    // the tree data is still loading).
    const timer = setTimeout(() => {
      setScrollToPathKey(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [
    revealInDepotIdent,
    workspace,
    depotLoaded,
    depotLoading,
    loadDepotTree,
    primitivesLoaded,
    primitivesLoading,
    loadPrimitives,
    catalogLoaded,
    catalogLoading,
    loadOACatalog,
  ]);

  const handleOpen = useCallback(
    (ident: schema.HierPathIdent) => {
      // User-initiated open from the depot tree: offer recover-vs-fresh if an
      // unsaved session is lingering (e.g. after a window reload mid-edit).
      void workspace.handleOpenRequest(ident, { promptOnUnsavedSession: true });
    },
    [workspace]
  );

  const handleOpenOA = useCallback(
    (ident: schema.LibCellViewIdent) => {
      // Highlight the opened OA item (parity with depot selection highlight).
      setSelectedIdent(ident);
      void workspace.handleOpenRequest(ident, { promptOnUnsavedSession: true });
    },
    [workspace]
  );

  const handleSelect = useCallback((ident: schema.HierPathIdent | null) => {
    setSelectedIdent(ident);
  }, []);

  const handleTreeContextMenu = useCallback(
    (e: React.MouseEvent, node: DepotTreeNode, fullPath: string) => {
      // Suppress the native browser menu for any depot node. This keeps explorer
      // right-click consistent before EditorCore's global handler is installed.
      e.preventDefault();
      setPrimContextMenu(null);
      setOaContextMenu(null);
      // Folders get a "New Design..." menu that pre-scopes the create dialog to
      // this folder's path; design leaves get the open/delete/export menu.
      if (node.nodeKind === "library") {
        setContextMenu(null);
        setFolderContextMenu({ x: e.clientX, y: e.clientY, path: fullPath });
        return;
      }
      setSelectedIdent(node.ident);
      setFolderContextMenu(null);
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        ident: node.ident,
        designNode: node,
      });
    },
    []
  );

  const handleCloseFolderContextMenu = useCallback(() => {
    setFolderContextMenu(null);
  }, []);

  const handleNewDesignFromFolder = useCallback(
    (path: string) => {
      workspace.openCreateDesignDialog(path);
      setFolderContextMenu(null);
    },
    [workspace]
  );

  const handleDeleteFromMenu = useCallback(
    (ident: schema.HierPathIdent) => {
      workspace.openDeleteDesignDialog(ident);
      setContextMenu(null);
      setSelectedIdent(null);
    },
    [workspace]
  );

  const handleDuplicateFromMenu = useCallback(
    (ident: schema.HierPathIdent) => {
      workspace.openDuplicateDialog(ident);
      setContextMenu(null);
      setSelectedIdent(null);
    },
    [workspace]
  );

  const handleRenameFromMenu = useCallback(
    (ident: schema.HierPathIdent) => {
      workspace.openRenameDialog(ident);
      setContextMenu(null);
      setSelectedIdent(null);
    },
    [workspace]
  );

  const handleAddToDesign = useCallback(
    (ident: schema.HierPathIdent) => {
      const snapshot = workspace.getSnapshot();
      if (!snapshot.activeIdent) return;
      // Prevent adding a design to itself
      if (Ident.equals(snapshot.activeIdent, ident)) return;
      const position = workspace.getViewportCenter();
      void workspace.createInstanceFromDepot({
        designIdent: snapshot.activeIdent,
        targetIdent: ident,
        position,
      });
    },
    [workspace]
  );

  // Show a context menu for an OpenAccess (lib, cell, view) item.
  const handleOAContextMenu = useCallback(
    (e: React.MouseEvent, ident: schema.LibCellViewIdent) => {
      e.preventDefault();
      // Close the other context menus so only one is open at a time.
      setContextMenu(null);
      setPrimContextMenu(null);
      setOaContextMenu({ x: e.clientX, y: e.clientY, ident });
    },
    []
  );

  const handleCloseOAContextMenu = useCallback(() => {
    setOaContextMenu(null);
  }, []);

  // Add an OpenAccess design to the active design. Mirrors handleAddToDesign but
  // targets a libcellview ident (createInstanceFromDepot accepts any Ident).
  const handleAddOAToDesign = useCallback(
    (ident: schema.LibCellViewIdent) => {
      const snapshot = workspace.getSnapshot();
      if (!snapshot.activeIdent) return;
      // Prevent adding a design to itself
      if (Ident.equals(snapshot.activeIdent, ident)) return;
      const position = workspace.getViewportCenter();
      void workspace.createInstanceFromDepot({
        designIdent: snapshot.activeIdent,
        targetIdent: ident,
        position,
      });
    },
    [workspace]
  );

  // Handler for adding a primitive (generator/builtin) to the active design.
  // For parametric primitives, this opens the add-instance dialog with the symbol pre-selected.
  // For non-parametric builtins, it creates the instance directly.
  const handleAddPrimitive = useCallback(
    (sym: SymbolInfo) => {
      const snapshot = workspace.getSnapshot();
      if (!snapshot.activeIdent) return;
      const position = workspace.getViewportCenter();
      if (sym.hasParams) {
        // Open the instance dialog with the symbol pre-selected
        workspace.openAddInstanceDialogWithSymbol(
          snapshot.activeIdent,
          position,
          sym
        );
      } else {
        // Create directly for non-parametric builtins
        void workspace.createInstance({
          designIdent: snapshot.activeIdent,
          symbolName: sym.name,
          pdkId: sym.pdkId,
          position,
        });
      }
    },
    [workspace]
  );

  // Show a context menu for a PDK primitive symbol
  const handlePrimitiveContextMenu = useCallback(
    (e: React.MouseEvent, sym: SymbolInfo) => {
      // Close any open depot / OA context menu
      setContextMenu(null);
      setOaContextMenu(null);
      setPrimContextMenu({ x: e.clientX, y: e.clientY, sym });
    },
    []
  );

  const handleClosePrimContextMenu = useCallback(() => {
    setPrimContextMenu(null);
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Delete key handler for selected design. Only depot designs (hierpath idents)
  // can be deleted; an OpenAccess (libcellview) selection is ignored here.
  const handleTreeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedIdent &&
        selectedIdent.kind === "hierpath"
      ) {
        e.preventDefault();
        workspace.openDeleteDesignDialog(selectedIdent);
        setSelectedIdent(null);
      }
    },
    [selectedIdent, workspace]
  );

  const clearFilter = () => {
    setFilter("");
    searchRef.current?.focus();
  };

  // Refresh both sections (only if they were previously loaded)
  const handleRefresh = useCallback(() => {
    if (depotLoaded) void loadDepotTree();
    if (catalogLoaded) void loadOACatalog();
  }, [depotLoaded, catalogLoaded, loadDepotTree, loadOACatalog]);

  const match = useMemo(() => buildPathMatcher(filter), [filter]);
  // Only filter the section that is expanded - keeps search results separate
  const depotExpanded = expandedKeys.has("depot");
  const oaExpanded = expandedKeys.has("openaccess");
  // Apply text filter and/or compatibility filter to depot roots
  let visibleRoots = roots;
  if (filter && depotExpanded) {
    visibleRoots = visibleRoots.filter((n) => nodeMatchesFilter(n, match, ""));
  }
  if (compatFilter && activeDesignInfo) {
    visibleRoots = filterCompatibleNodes(visibleRoots, activeDesignInfo);
  }

  // Apply compatibility filter to primitives
  const primitivesExpanded = expandedKeys.has("primitives");
  const builtInExpanded = expandedKeys.has("builtin");
  const visiblePrimitiveGroups =
    compatFilter && activeDesignInfo
      ? primitiveGroups
          .map((group) => ({
            ...group,
            symbols: group.symbols.filter((sym) =>
              isCompatiblePrimitive(sym, activeDesignInfo)
            ),
          }))
          .filter((group) => group.symbols.length > 0)
      : primitiveGroups;

  // Split primitives into PDK-specific groups and PDK-independent built-ins so
  // they can be shown in separate sections.
  const pdkPrimitiveGroups = visiblePrimitiveGroups.filter((g) => !g.isBuiltIn);
  const builtInPrimitiveGroups = visiblePrimitiveGroups.filter(
    (g) => g.isBuiltIn
  );
  // The built-in section renders its symbols flat (there is only ever one
  // built-in group), optionally text-filtered.
  const builtInSymbols = builtInPrimitiveGroups.flatMap((g) =>
    builtInExpanded && filter
      ? g.symbols.filter((s) => match(s.name))
      : g.symbols
  );

  // Whether "Add To Design" is available for primitives
  const canAddPrimitive =
    !!activeDesignInfo &&
    (activeDesignInfo.contentKind === "layout" ||
      activeDesignInfo.contentKind === "schematic");

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Search + refresh row */}
      <div className="px-2 py-1.5 border-b border-[var(--color-ide-border)] flex items-center gap-1">
        <div className="flex flex-1 items-center gap-1.5 bg-[var(--color-ide-bg)] border border-[var(--color-ide-border)] rounded px-2 py-1">
          <Search
            size={11}
            className="text-[var(--color-ide-text-muted)] shrink-0"
          />
          <input
            ref={searchRef}
            type="text"
            placeholder="Filter designs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 bg-transparent text-xs text-[var(--color-ide-text)] placeholder-[var(--color-ide-text-dim)] outline-none font-mono"
          />
          {filter && (
            <button
              onClick={clearFilter}
              className="text-[var(--color-ide-text-muted)] hover:text-[var(--color-ide-text)]"
            >
              <X size={11} />
            </button>
          )}
        </div>
        <button
          onClick={() => setCompatFilter((prev) => !prev)}
          title={
            compatFilter ? "Show all designs" : "Show only compatible designs"
          }
          className="ide-panel-header-btn"
          style={
            compatFilter && activeDesignInfo
              ? { color: "var(--color-ide-accent)" }
              : activeDesignInfo
              ? {}
              : { opacity: 0.3, pointerEvents: "none" }
          }
        >
          <Filter size={12} />
        </button>
        <button
          onClick={handleRefresh}
          title="Refresh"
          className={`ide-panel-header-btn${depotLoading ? " ide-spin" : ""}`}
          style={depotLoading ? { color: "var(--color-ide-accent)" } : {}}
        >
          <RefreshCw size={12} />
        </button>
        <button
          onClick={() => workspace.openCreateDesignDialog()}
          title="New Design"
          className="ide-panel-header-btn"
        >
          <Plus size={12} />
        </button>
      </div>

      {/* Tree */}
      <ScrollArea.Root className="flex-1 min-h-0">
        <ScrollArea.Viewport ref={scrollViewportRef} className="h-full w-full">
          <div
            ref={treeRef}
            className="py-1 outline-none"
            tabIndex={0}
            onKeyDown={handleTreeKeyDown}
          >
            {/* PDK Primitives + Built-In Primitives sections. Only shown when a
                design is active — the user can't add a primitive to anything
                otherwise, so an empty design has nothing to act on here. */}
            {activeDesignInfo && (
              <>
                {/* PDK Primitives Section */}
                <div>
                  <div
                    className="flex items-center gap-1 py-1 px-2 cursor-pointer select-none
                               hover:bg-white/5 text-[var(--color-ide-text)]"
                    onClick={() => {
                      onToggleExpand("primitives");
                    }}
                  >
                    <span className="w-3 shrink-0">
                      {expandedKeys.has("primitives") ? (
                        <ChevronDown size={12} />
                      ) : (
                        <ChevronRight size={12} />
                      )}
                    </span>
                    <Zap
                      size={14}
                      className="shrink-0 text-[var(--color-ide-text-muted)]"
                    />
                    <span className="text-xs font-medium font-mono">
                      PDK Primitives
                    </span>
                  </div>
                  {primitivesExpanded && (
                    <div>
                      {primitivesLoading && primitiveGroups.length === 0 ? (
                        <div className="px-4 py-2 text-xs text-[var(--color-ide-text-muted)]">
                          Loading...
                        </div>
                      ) : pdkPrimitiveGroups.length === 0 ? (
                        <div className="px-4 py-2 text-xs text-[var(--color-ide-text-muted)]">
                          {filter ? "No matches" : "No primitives"}
                        </div>
                      ) : (
                        <FadeIn>
                          <div>
                            {pdkPrimitiveGroups.map((group) => (
                              <PrimitiveGroupNode
                                key={group.pdkId}
                                group={group}
                                depth={1}
                                filter={filter}
                                match={match}
                                expandedKeys={expandedKeys}
                                onToggleExpand={onToggleExpand}
                                onAddToDesign={handleAddPrimitive}
                                onContextMenu={handlePrimitiveContextMenu}
                                addEnabled={canAddPrimitive}
                                scrollToPathKey={scrollToPathKey}
                              />
                            ))}
                          </div>
                        </FadeIn>
                      )}
                    </div>
                  )}
                </div>

                {/* Built-In Primitives Section. PDK-independent primitives are
                    listed flat (no per-PDK grouping folder). */}
                <div>
                  <div
                    className="flex items-center gap-1 py-1 px-2 cursor-pointer select-none
                               hover:bg-white/5 text-[var(--color-ide-text)]"
                    onClick={() => {
                      onToggleExpand("builtin");
                    }}
                  >
                    <span className="w-3 shrink-0">
                      {expandedKeys.has("builtin") ? (
                        <ChevronDown size={12} />
                      ) : (
                        <ChevronRight size={12} />
                      )}
                    </span>
                    <Boxes
                      size={14}
                      className="shrink-0 text-[var(--color-ide-text-muted)]"
                    />
                    <span className="text-xs font-medium font-mono">
                      Built-In Primitives
                    </span>
                  </div>
                  {builtInExpanded && (
                    <div>
                      {primitivesLoading && primitiveGroups.length === 0 ? (
                        <div className="px-4 py-2 text-xs text-[var(--color-ide-text-muted)]">
                          Loading...
                        </div>
                      ) : builtInSymbols.length === 0 ? (
                        <div className="px-4 py-2 text-xs text-[var(--color-ide-text-muted)]">
                          {filter ? "No matches" : "No primitives"}
                        </div>
                      ) : (
                        <FadeIn>
                          <div>
                            {builtInSymbols.map((sym) => (
                              <PrimitiveLeafNode
                                key={sym.name}
                                sym={sym}
                                depth={1}
                                onAddToDesign={handleAddPrimitive}
                                onContextMenu={handlePrimitiveContextMenu}
                                addEnabled={canAddPrimitive}
                                scrollToPathKey={scrollToPathKey}
                                pathKey={`builtin/${sym.name}`}
                              />
                            ))}
                          </div>
                        </FadeIn>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Depot Section */}
            <div>
              <div
                className="flex items-center gap-1 py-1 px-2 cursor-pointer select-none
                           hover:bg-white/5 text-[var(--color-ide-text)]"
                onClick={() => onToggleExpand("depot")}
              >
                <span className="w-3 shrink-0">
                  {expandedKeys.has("depot") ? (
                    <ChevronDown size={12} />
                  ) : (
                    <ChevronRight size={12} />
                  )}
                </span>
                <span className="text-xs font-medium font-mono">Depot</span>
              </div>
              {expandedKeys.has("depot") && (
                <div>
                  {depotLoading && roots.length === 0 ? (
                    <div className="px-4 py-2 text-xs text-[var(--color-ide-text-muted)]">
                      Loading...
                    </div>
                  ) : depotError ? (
                    <div className="px-4 py-2 text-xs text-[var(--color-ide-error)]">
                      {depotError}
                    </div>
                  ) : visibleRoots.length === 0 ? (
                    <div className="px-4 py-2 text-xs text-[var(--color-ide-text-muted)]">
                      {filter ? "No matches" : "No designs"}
                    </div>
                  ) : (
                    <FadeIn>
                      <div>
                        {visibleRoots.map((node, i) => (
                          <TreeNodeView
                            key={`${node.name}-${i}`}
                            node={node}
                            depth={1}
                            pathKey={`depot/${node.name}`}
                            nodePath=""
                            filter={depotExpanded ? filter : ""}
                            match={match}
                            onOpen={handleOpen}
                            onSelect={handleSelect}
                            selectedIdent={selectedIdent}
                            onContextMenu={handleTreeContextMenu}
                            expandedKeys={expandedKeys}
                            onToggleExpand={onToggleExpand}
                            scrollToPathKey={scrollToPathKey}
                          />
                        ))}
                      </div>
                    </FadeIn>
                  )}
                </div>
              )}
            </div>

            {/* OpenAccess Section - only shown if catalog has libraries */}
            {oaAvailable && (
              <div>
                <div
                  className="flex items-center gap-1 py-1 px-2 cursor-pointer select-none
                           hover:bg-white/5 text-[var(--color-ide-text)]"
                  onClick={() => onToggleExpand("openaccess")}
                >
                  <span className="w-3 shrink-0">
                    {expandedKeys.has("openaccess") ? (
                      <ChevronDown size={12} />
                    ) : (
                      <ChevronRight size={12} />
                    )}
                  </span>
                  <span className="text-xs font-medium font-mono">
                    OpenAccess
                  </span>
                </div>
                {expandedKeys.has("openaccess") && (
                  <FadeIn>
                    <div>
                      {(filter && oaExpanded
                        ? catalog.libraries.filter((lib) =>
                            oaLibraryMatchesFilter(lib, match, "")
                          )
                        : catalog.libraries
                      ).map((lib) => (
                        <OALibraryNode
                          key={lib.name}
                          library={lib}
                          depth={1}
                          filter={oaExpanded ? filter : ""}
                          match={match}
                          onOpen={handleOpenOA}
                          onContextMenu={handleOAContextMenu}
                          expandedKeys={expandedKeys}
                          onToggleExpand={onToggleExpand}
                          selectedIdent={selectedIdent}
                          scrollToPathKey={scrollToPathKey}
                        />
                      ))}
                    </div>
                  </FadeIn>
                )}
              </div>
            )}
          </div>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          className="flex w-1.5 touch-none select-none p-px"
          orientation="vertical"
        >
          <ScrollArea.Thumb className="relative flex-1 rounded-full bg-[var(--color-ide-border)]" />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>

      {/* Right-click context menu for depot designs */}
      {contextMenu && (
        <DesignContextMenu
          menu={contextMenu}
          onClose={handleCloseContextMenu}
          onOpen={handleOpen}
          onDelete={handleDeleteFromMenu}
          onDuplicate={handleDuplicateFromMenu}
          onRename={handleRenameFromMenu}
          onAddToDesign={handleAddToDesign}
          addToDesignEnabled={
            !!activeDesignInfo &&
            !!contextMenu.designNode &&
            isCompatibleDesign(contextMenu.designNode, activeDesignInfo) &&
            // Prevent adding a design to itself
            !(
              activeIdent &&
              Ident.stringify(activeIdent) ===
                Ident.stringify(contextMenu.designNode.ident)
            )
          }
        />
      )}
      {/* Right-click context menu for depot folders */}
      {folderContextMenu && (
        <FolderContextMenu
          menu={folderContextMenu}
          onClose={handleCloseFolderContextMenu}
          onNewDesign={handleNewDesignFromFolder}
        />
      )}
      {/* Right-click context menu for PDK primitives */}
      {primContextMenu && (
        <PrimitiveContextMenu
          menu={primContextMenu}
          onClose={handleClosePrimContextMenu}
          onAddToDesign={handleAddPrimitive}
          addEnabled={canAddPrimitive}
        />
      )}
      {/* Right-click context menu for OpenAccess items */}
      {oaContextMenu && (
        <OAContextMenu
          menu={oaContextMenu}
          onClose={handleCloseOAContextMenu}
          onOpen={handleOpenOA}
          onAddToDesign={handleAddOAToDesign}
          addToDesignEnabled={canAddPrimitive}
        />
      )}
    </div>
  );
}
